/**
 * A porta entre a tela de manutenção e a API do módulo 05.
 *
 * É o único arquivo do módulo que conhece caminho de rota e formato de JSON. O
 * `fetch` e a queda de erro moram em `@/lib/pedido`.
 */
import { comCorpo, pedir } from '@/lib/pedido';
import type { ParadaProgramada } from '@/modulos/oficina/api';

/* ==========================================================================
 * As linhas, exatamente como a API as devolve
 * ======================================================================= */

export type Gatilho = 'horas' | 'km' | 'dias';

export type EstadoDoVencimento = 'vencida' | 'vence_logo' | 'em_dia' | 'sem_leitura' | 'sem_ponto_de_partida';

export type Confianca = 'alta' | 'media' | 'baixa';

/** Uma máquina × uma tarefa de plano publicado que a alcança. */
export interface VencimentoDaApi {
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly planoId: string;
  readonly plano: string;
  readonly alcancaPor: string;
  readonly tarefaId: string;
  readonly descricao: string;
  readonly gatilho: Gatilho;
  readonly intervalo: number;
  readonly avisoAntes: number;
  readonly ultimaRealizadaEm: string | null;
  readonly ultimaLeituraRealizada: number | null;
  readonly leituraAtual: number | null;
  readonly leituraAtualEm: string | null;
  readonly ritmoDia: number | null;
  readonly venceNaLeitura: number | null;
  /** Na unidade do gatilho: horas, km ou dias. Negativo = passou. */
  readonly faltam: number | null;
  /** `aaaa-mm-dd`. Projetada a partir da DATA DA LEITURA, não de hoje. */
  readonly previstaPara: string | null;
  readonly estado: EstadoDoVencimento;
  readonly confianca: Confianca | null;
  /**
   * A O.S. aberta desta tarefa nesta máquina, com a parada combinada com a
   * operação (0089). Ausente em servidor anterior a esta mudança.
   */
  readonly ordemAberta?: OrdemDoVencimento | null;
}

export interface OrdemDoVencimento {
  readonly id: string;
  readonly numero: number;
  readonly etapa: string;
  readonly paradaProgramada: ParadaProgramada | null;
}

export type SituacaoDoPlano = 'rascunho' | 'publicado' | 'arquivado';

export interface PlanoDaApi {
  readonly id: string;
  readonly nome: string;
  readonly situacao: SituacaoDoPlano;
  readonly alvoTipo: 'modelo' | 'configuracao' | 'equipamento';
  readonly alvoId: string;
  readonly alvo: string | null;
  readonly documentoId: string | null;
  readonly documento: string | null;
  readonly substituiId: string | null;
  readonly revisaoNota: string | null;
  readonly publicadoEm: string | null;
  readonly publicadoPor: string | null;
  readonly arquivadoEm: string | null;
  readonly observacao: string | null;
  readonly criadoEm: string;
  readonly tarefas: number;
  readonly revisaoAbertaId: string | null;
}

export interface TarefaDaApi {
  readonly id: string;
  readonly chave: string;
  readonly ordem: number;
  readonly descricao: string;
  readonly gatilho: Gatilho;
  readonly intervalo: number;
  readonly avisoAntes: number | null;
  readonly pagina: number | null;
  readonly procedimento: string | null;
  /** A lista técnica (0021): o que a tarefa consome. */
  readonly itens: readonly ItemDaTarefaDaApi[];
}

export interface ItemDaTarefaDaApi {
  readonly id: string;
  readonly produtoId: string;
  readonly produto: string;
  readonly produtoCodigo: string;
  readonly unidade: string;
  readonly quantidade: number;
  /** 0088 · aviso, nunca bloqueio: a peça não tem aplicação validada para a máquina do plano. */
  readonly semAplicacaoValidada?: boolean;
}

export interface RealizacaoDaApi {
  readonly id: string;
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly tarefaId: string;
  readonly tarefa: string;
  readonly realizadaEm: string;
  readonly leitura: number | null;
  readonly origem: string;
  readonly evidencia: string;
  readonly corrigeId: string | null;
  readonly corrigida: boolean;
  readonly registradoPor: string | null;
  readonly criadoEm: string;
}

export interface CadastrosDaApi {
  readonly modelos: readonly { id: string; nome: string }[];
  readonly configuracoes: readonly { id: string; nome: string; modelo: string }[];
  /** O código interno e a placa vão nas etiquetas do seletor da máquina (31/10/2026). */
  readonly equipamentos: readonly { id: string; codigo: string; descricao: string; codigoInterno?: string | null; placa?: string | null }[];
  readonly documentos: readonly { id: string; titulo: string; situacao: string }[];
  readonly produtos: readonly { id: string; codigo: string; nome: string; unidade: string }[];
}

export interface EnvioDeTarefa {
  readonly descricao: string;
  readonly gatilho: Gatilho;
  readonly intervalo: number;
  readonly avisoAntes?: number | null;
  readonly pagina?: number | null;
}

export interface EnvioDePlano {
  readonly nome: string;
  readonly alvo: { readonly tipo: PlanoDaApi['alvoTipo']; readonly id: string };
  readonly documentoId?: string | null;
  readonly tarefas: readonly EnvioDeTarefa[];
}

export interface EnvioDeRealizacao {
  readonly equipamentoId: string;
  readonly tarefaId: string;
  readonly realizadaEm: string;
  readonly leitura?: number | null;
  readonly evidencia: string;
  readonly corrigeId?: string | null;
}

export const api = {
  cadastros: () => pedir<CadastrosDaApi>('/manutencao/cadastros'),

  vencimentos: (equipamentoId?: string) =>
    pedir<{ vencimentos: readonly VencimentoDaApi[] }>(
      equipamentoId ? `/manutencao/vencimentos?equipamentoId=${encodeURIComponent(equipamentoId)}` : '/manutencao/vencimentos',
    ),

  planos: () => pedir<{ planos: readonly PlanoDaApi[] }>('/manutencao/planos'),

  plano: (id: string) =>
    pedir<{ plano: PlanoDaApi; tarefas: readonly TarefaDaApi[] }>(`/manutencao/planos/${encodeURIComponent(id)}`),

  realizacoes: (equipamentoId: string) =>
    pedir<{ realizacoes: readonly RealizacaoDaApi[] }>(
      `/manutencao/realizacoes?equipamentoId=${encodeURIComponent(equipamentoId)}`,
    ),

  criarPlano: (envio: EnvioDePlano) => comCorpo<{ id: string }>('POST', '/manutencao/planos', envio),
  /** IA 02: o rascunho extraído do manual, conferido tarefa a tarefa no servidor. */
  extrairPlano: (envio: { documentoId: string; alvo: { tipo: PlanoDaApi['alvoTipo']; id: string }; nome?: string }) =>
    comCorpo<{ criado: boolean; planoId?: string; tarefas?: number; descartadas: number; falta?: string | null }>(
      'POST',
      '/manutencao/planos/rascunho-da-ia',
      envio,
    ),

  acrescentarTarefa: (planoId: string, envio: EnvioDeTarefa) =>
    comCorpo<{ id: string }>('POST', `/manutencao/planos/${encodeURIComponent(planoId)}/tarefas`, envio),

  acrescentarItem: (tarefaId: string, envio: { produtoId: string; quantidade: number }) =>
    comCorpo<{ id: string }>('POST', `/manutencao/tarefas/${encodeURIComponent(tarefaId)}/itens`, envio),

  tirarItem: (itemId: string) => comCorpo<{ removido: boolean }>('DELETE', `/manutencao/itens/${encodeURIComponent(itemId)}`),

  tirarTarefa: (tarefaId: string) =>
    comCorpo<{ removida: boolean }>('DELETE', `/manutencao/tarefas/${encodeURIComponent(tarefaId)}`),

  publicar: (planoId: string, revisaoNota: string) =>
    comCorpo<{ arquivou: string | null }>('POST', `/manutencao/planos/${encodeURIComponent(planoId)}/publicacao`, {
      revisaoNota,
    }),

  revisar: (planoId: string) =>
    comCorpo<{ id: string }>('POST', `/manutencao/planos/${encodeURIComponent(planoId)}/revisao`, {}),

  arquivar: (planoId: string, motivo: string) =>
    comCorpo<{ id: string }>('POST', `/manutencao/planos/${encodeURIComponent(planoId)}/arquivamento`, { motivo }),

  registrarRealizacao: (envio: EnvioDeRealizacao) =>
    comCorpo<{ id: string }>('POST', '/manutencao/realizacoes', envio),
};
